'use client';

/**
 * A figure whose digits roll to their new value instead of being replaced.
 *
 * After React Bits' Counter (MIT + Commons Clause, used inside the product as
 * its licence allows): every decimal place is its own column of ten numerals
 * on a cylinder, driven by one number — that place's rolled value — so a total
 * going from $3,448 to $4,097 visibly carries: the thousands roll one, the
 * hundreds roll the long way round, and nothing re-renders to do it.
 *
 * Driven by THE spring (`lib/motion/spring.ts`), so a total that changes
 * again while it is still rolling keeps its momentum instead of restarting,
 * and it settles exactly like every panel on the site.
 *
 * It is a READOUT (AGENTS.md "Status-readout rule"): no hover, no pointer,
 * nothing to press. Screen readers get the formatted figure as text; the
 * columns are hidden from them.
 */

import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSpring } from '@/lib/motion/useSpring';
import { usePrefersReducedMotion } from '@/lib/usePrefersReducedMotion';

interface Props {
  value: number;
  /** Digits after the decimal point, fixed (a figure does not change width
   *  because its last digit happens to be 0). */
  decimals?: number;
  /** Written before the digits, e.g. "$". */
  prefix?: string;
  /** Written after the digits, e.g. " dB". */
  suffix?: string;
  /** Thousands separators. */
  group?: boolean;
  className?: string;
  testId?: string;
}

const MINUS = '−';

/** Where numeral `n` sits (in column heights) when the column reads `current`:
 *  within five either way, so every roll takes the short way round. */
function offsetOf(n: number, current: number): number {
  const place = ((current % 10) + 10) % 10;
  let offset = (10 + n - place) % 10;
  if (offset > 5) offset -= 10;
  return offset;
}

function Digit({ rolled, reduce }: { rolled: number; reduce: boolean }) {
  const spring = useSpring(rolled, { restDelta: 0.001, restSpeed: 0.01 });
  const numerals = useRef<(HTMLSpanElement | null)[]>([]);

  // Frozen at mount for the same reason as ViewSwitch's thumb: a style React
  // re-sends on a later render would overwrite a roll in progress.
  const [initial] = useState(() =>
    Array.from({ length: 10 }, (_, n) => `translateY(${offsetOf(n, rolled) * 100}%)`),
  );

  const paint = useCallback((current: number) => {
    for (let n = 0; n < 10; n++) {
      const el = numerals.current[n];
      if (el) el.style.transform = `translateY(${offsetOf(n, current) * 100}%)`;
    }
  }, []);

  useLayoutEffect(() => spring.onChange(paint), [spring, paint]);

  useEffect(() => {
    if (reduce) spring.jump(rolled);
    else spring.to(rolled);
  }, [rolled, reduce, spring]);

  return (
    <span
      className="relative inline-block h-[1.2em] w-[1ch] overflow-hidden"
      style={{
        // Soft top and bottom so a numeral rolls in rather than appearing at a
        // hard edge. At rest the glyph sits well inside the unfaded band.
        maskImage: 'linear-gradient(to bottom, transparent, black 14%, black 86%, transparent)',
        WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 14%, black 86%, transparent)',
      }}
    >
      {initial.map((transform, n) => (
        <span
          key={n}
          ref={(el) => { numerals.current[n] = el; }}
          className="absolute inset-0 flex items-center justify-center"
          style={{ transform }}
        >
          {n}
        </span>
      ))}
    </span>
  );
}

export function formatRolling(
  value: number,
  { decimals = 0, prefix = '', suffix = '', group = true }: Omit<Props, 'value' | 'className' | 'testId'> = {},
): string {
  const body = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: group,
  }).format(Math.abs(value));
  return `${value < 0 ? MINUS : ''}${prefix}${body}${suffix}`;
}

export default function RollingNumber({
  value, decimals = 0, prefix = '', suffix = '', group = true, className = '', testId,
}: Props) {
  const reduce = usePrefersReducedMotion();
  const scale = 10 ** decimals;
  // Integer arithmetic from here on: 0.1 + 0.2 must never roll a digit.
  const scaled = Math.round(Math.abs(value) * scale);
  const intDigits = String(Math.floor(scaled / scale)).length;
  const places = intDigits + decimals;
  const negative = value < 0 && scaled !== 0;

  const columns = [];
  for (let k = places - 1; k >= 0; k--) {
    const exponent = k - decimals; // this column's power of ten in the real value
    columns.push(
      <Fragment key={k}>
        <Digit rolled={Math.floor(scaled / 10 ** k)} reduce={reduce} />
        {group && exponent > 0 && exponent % 3 === 0 && <span>,</span>}
        {decimals > 0 && exponent === 0 && <span>.</span>}
      </Fragment>,
    );
  }

  return (
    <span className={`relative inline-flex items-center ${className}`} data-testid={testId}>
      <span className="sr-only">{formatRolling(value, { decimals, prefix, suffix, group })}</span>
      <span aria-hidden className="ed-mono inline-flex select-none items-center leading-none">
        {negative && <span>{MINUS}</span>}
        {prefix && <span className="whitespace-pre">{prefix}</span>}
        {columns}
        {suffix && <span className="whitespace-pre">{suffix}</span>}
      </span>
    </span>
  );
}
